import Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, titleStyle, readoutStyle, FONT, px } from '../theme';
import { createButton } from '../ui/button';
import { createListRow } from '../ui/panel';
import { createTextInput } from '../ui/textInput';
import { Rng, generateSeed } from '@core/rng';
import {
  BACKGROUNDS,
  PORTRAIT_IDS,
  createCharacter,
  randomName,
  type Background,
  type Character,
} from '@core/character/character';
import { Campaign } from '@core/campaign/campaign';
import { drawFallbackPortrait } from '../render/portrait';
import { portraitKey } from '../assets';
import type { MapData } from '../mapData';

/**
 * Charaktererstellung.
 *
 * Statt sechs Reglern auf gezeichneten Ebenen steht hier ein Raster fertiger
 * Portraets. Der Grund ist nicht Bequemlichkeit: Die Regler sahen nach
 * Platzhalter aus, und der Editor ist das Erste, was man vom Spiel sieht.
 * Fertige Aufnahmen kosten dafuer die freie Kombinierbarkeit — bei einer Figur,
 * die man im Spiel fast nie sieht, ist das der bessere Handel.
 *
 * Die Herkunft ist dagegen kein Beiwerk. Sie steht mit ihrer Wirkung und ihrem
 * Verlust ausgeschrieben da — beides begleitet die Figur bis ins Finale.
 */
export class CharacterCreatorScene extends Phaser.Scene {
  private character!: Character;

  constructor() {
    super('CharacterCreator');
  }

  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE.abyss);
    this.character = createCharacter(Rng.fromSeed(generateSeed()));
    this.redraw();
    this.cameras.main.fadeIn(320, 5, 8, 13);
  }

  private update_(changes: Partial<Character>): void {
    this.character = { ...this.character, ...changes };
    this.redraw();
  }

  private redraw(): void {
    this.children.removeAll();

    const { width, height } = this.scale;
    const centreX = width / 2;
    // Breit genug, dass ein Gesicht im Raster erkennbar bleibt. Bei 340 px
    // waren die Kacheln 63 px breit — man konnte die Portraets nicht
    // auseinanderhalten und waehlte blind.
    const columnWidth = Math.min(430, width / 2 - 60);
    const leftColumn = centreX - columnWidth - 32;
    const rightColumn = centreX + 32;

    this.add.text(centreX, 40, t('character.title'), titleStyle(24)).setOrigin(0.5);

    const gridBottom = this.renderPortraitGrid(leftColumn, 92, columnWidth);
    this.renderName(leftColumn, gridBottom + 24, columnWidth);
    this.renderBackground(rightColumn, 92, columnWidth);

    createButton(this, centreX - 110, height - 52, t('character.randomise'), () => {
      this.character = createCharacter(Rng.fromSeed(generateSeed()));
      this.redraw();
    }, { width: 190 });

    createButton(this, centreX + 110, height - 52, t('character.begin'), () => this.begin(), {
      width: 190,
    });
  }

  /**
   * Das Auswahlraster.
   *
   * Fuenf Spalten, drei Reihen — alle fuenfzehn Portraets auf einen Blick, ohne
   * Blaettern. Das Gewaehlte bekommt einen hellen Rahmen; die uebrigen liegen
   * leicht abgedunkelt darunter, damit die Wahl auch ohne Farbe lesbar ist.
   */
  private renderPortraitGrid(left: number, top: number, columnWidth: number): number {
    this.add.text(left, top, t('character.portrait'), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: PALETTE.inkFaint,
    });

    const columns = 5;
    const gap = 6;
    const cell = Math.floor((columnWidth - gap * (columns - 1)) / columns);
    const cellHeight = Math.round(cell * 1.25);

    PORTRAIT_IDS.forEach((id, index) => {
      const x = left + (index % columns) * (cell + gap);
      const y = top + 20 + Math.floor(index / columns) * (cellHeight + gap);
      const chosen = this.character.portraitId === id;

      const image = this.add
        .image(x, y, this.textureFor(id))
        .setOrigin(0, 0)
        .setDisplaySize(cell, cellHeight)
        .setAlpha(chosen ? 1 : 0.55)
        .setInteractive({ useHandCursor: true });

      image.on('pointerover', () => {
        if (!chosen) image.setAlpha(0.85);
      });
      image.on('pointerout', () => {
        if (!chosen) image.setAlpha(0.55);
      });
      image.on('pointerdown', () => this.update_({ portraitId: id }));

      this.add
        .rectangle(x, y, cell, cellHeight)
        .setOrigin(0, 0)
        .setStrokeStyle(chosen ? 2 : 1, chosen ? PALETTE.coast : PALETTE.panelEdge);
    });

    const rows = Math.ceil(PORTRAIT_IDS.length / columns);
    return top + 20 + rows * (cellHeight + gap) - gap;
  }

  /**
   * Der Texturschluessel eines Portraets — oder ein gezeichneter Ersatz.
   *
   * Ohne diesen Rueckfall bliebe der Editor leer, sobald die Bilddateien
   * fehlen, etwa in einem frischen Arbeitsverzeichnis, in dem
   * `tools/fetch_assets.py` noch nicht gelaufen ist.
   */
  private textureFor(id: string): string {
    const key = portraitKey(id);
    if (this.textures.exists(key)) return key;

    const fallback = `portrait-fallback:${id}`;
    if (!this.textures.exists(fallback)) {
      this.textures.addCanvas(fallback, drawFallbackPortrait(id, 128));
    }
    return fallback;
  }

  private renderName(left: number, y: number, columnWidth: number): void {
    this.add.text(left, y, t('character.name'), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: PALETTE.inkFaint,
    });

    // Eingabefeld statt fester Anzeige: Der eigene Name gehoert der Spielerin.
    // Der Wuerfel bleibt daneben, fuer alle, die nicht tippen wollen.
    createTextInput(this, left, y + 14, {
      width: columnWidth - 150,
      value: this.character.name,
      // Nicht `update_` verwenden: Ein Neuzeichnen bei jedem Tastendruck
      // wuerde das Feld und damit die Eingabe verlieren.
      onChange: (name) => {
        this.character = { ...this.character, name };
      },
    });

    createButton(
      this,
      left + columnWidth - 68,
      y + 31,
      t('character.name.reroll'),
      () => this.update_({ name: randomName(Rng.fromSeed(generateSeed())) }),
      { width: 130, height: 28, fontSize: 10 },
    );
  }

  private renderBackground(left: number, top: number, columnWidth: number): void {
    this.add.text(left, top, t('character.background'), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: PALETTE.inkFaint,
    });

    BACKGROUNDS.forEach((background, index) => {
      createListRow(this, left, top + 22 + index * 92, {
        width: columnWidth,
        height: 84,
        title: t(`background.${background}`),
        detail: t(`background.${background}.blurb`),
        selected: this.character.background === background,
        onClick: () => this.chooseBackground(background),
      });
    });

    const y = top + 22 + BACKGROUNDS.length * 92 + 12;

    this.add.text(left, y, t(`background.${this.character.background}.effect`), {
      ...readoutStyle(11),
      color: TEXT_COLOUR.base,
      wordWrap: { width: columnWidth },
    });

    // Der Verlust ist der Grund, warum die Figur überhaupt hier draußen ist.
    this.add.text(
      left,
      y + 34,
      t('character.lost', {
        name: this.character.lostName,
        relation: t(this.character.lostRelationKey),
      }),
      { ...readoutStyle(11), color: PALETTE.inkFaint, fontStyle: 'italic' },
    );
  }

  private chooseBackground(background: Background): void {
    this.update_({
      background,
      // Der Verlust haengt am Hintergrund und wechselt mit ihm.
      lostRelationKey: createCharacter(Rng.fromSeed('x'), { background }).lostRelationKey,
    });
  }

  private begin(): void {
    // Wer das Feld leert, bekommt wieder einen zufaelligen Namen — ohne Namen
    // laesst sich im Finale schlecht ueber jemanden sprechen.
    if (this.character.name.trim() === '') {
      this.character = { ...this.character, name: randomName(Rng.fromSeed(generateSeed())) };
    }

    const mapData = this.registry.get('mapData') as MapData;
    const seed = (this.registry.get('pendingSeed') as string | undefined) ?? generateSeed();

    const campaign = new Campaign({
      seed,
      bathymetry: mapData.bathymetry,
      character: this.character,
      // Die gezeichnete Kueste geht in die Wegsuche ein — sonst faehrt das
      // Schiff ueber Land, das auf der Karte deutlich sichtbar ist.
      coastline: mapData.coastline,
    });

    this.registry.set('campaign', campaign);
    // Gespeichert wird an der Station, nicht hier — der Introbildschirm liegt
    // dazwischen, und ein Stand mittendrin haette keinen Wert.
    this.scene.start('Intro');
  }
}
